import React from "react";
import { formatCompact } from "@/utils/formatters";

interface ChangeBadgeProps {
  value: number;
  /** How to format the value. Default "percent" shows "↑ 5.2%". "dollar" shows "↑ $1,250". "number" shows "↑ 3". */
  format?: "percent" | "dollar" | "number";
  /** Optional comparison period label, e.g. "vs prev 14d" */
  comparisonLabel?: string;
  /**
   * When true, flip the color logic: positive delta = red, negative = green.
   * Use for "lower is better" metrics: no-shows, orphan pre-calls, losses.
   * Default false = standard behavior (up = green, down = red).
   */
  invert?: boolean;
  /**
   * When true, render a neutral "= same" badge on a 0 delta instead of nothing.
   * Lets a flat metric read as "held flat" rather than "no comparison data".
   * Default false = return null on 0 (unchanged for dashboard/Mastermind consumers).
   */
  showZero?: boolean;
}

const ChangeBadge: React.FC<ChangeBadgeProps> = ({
  value,
  format = "percent",
  comparisonLabel,
  invert = false,
  showZero = false,
}) => {
  if (!value || value === 0) {
    if (showZero) {
      return (
        <span className="inline-flex items-center token-11 font-medium px-2 py-0.5 rounded-md text-glass-muted bg-black/[0.04]">
          = same
          {comparisonLabel && (
            <span className="ml-1 font-normal opacity-70">{comparisonLabel}</span>
          )}
        </span>
      );
    }
    return null;
  }

  const isPositive = value > 0;
  const arrow = isPositive ? "↑" : "↓";

  let displayValue: string;
  if (format === "dollar") {
    displayValue = formatCompact(Math.abs(value));
  } else if (format === "number") {
    displayValue = Math.abs(value).toString();
  } else {
    displayValue = `${Math.abs(value).toFixed(1)}%`;
  }

  // invert=true flips semantic color: rising is bad (red), falling is good (green)
  const isGood = invert ? !isPositive : isPositive;

  return (
    <span
      className="inline-flex items-center token-11 font-semibold px-2 py-0.5 rounded-md"
      style={{
        color: isGood ? "var(--green)" : "var(--red)",
        backgroundColor: isGood ? "var(--green-bg)" : "var(--red-bg)",
      }}
    >
      {arrow} {displayValue}
      {comparisonLabel && <span className="ml-1 font-normal opacity-70">{comparisonLabel}</span>}
    </span>
  );
};

export default ChangeBadge;
